import type { ViewspecState } from "../viewspecs/types.ts";
import { VIEWSPEC_PRESETS } from "../viewspecs/presets.ts";
import { useState } from "react";

interface ViewspecPanelProps {
  viewspecs: ViewspecState;
  onChange: (vs: Partial<ViewspecState>) => void;
  visible: boolean;
}

export function ViewspecPanel({
  viewspecs,
  onChange,
  visible,
}: ViewspecPanelProps) {
  const [filterInput, setFilterInput] = useState(viewspecs.filterText ?? "");

  if (!visible) return null;

  return (
    <div className="viewspec-panel">
      {/* Depth */}
      <h3>Depth</h3>
      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
        {[1, 2, 3, 4, 5].map((d) => (
          <button
            key={d}
            className={viewspecs.depthLimit === d ? "active" : ""}
            onClick={() => onChange({ depthLimit: d })}
          >
            {d}
          </button>
        ))}
        <button
          className={viewspecs.depthLimit === null ? "active" : ""}
          onClick={() => onChange({ depthLimit: null })}
        >
          All
        </button>
      </div>

      {/* Content */}
      <h3>Content</h3>
      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
        {(["all", "first-line", "none"] as const).map((mode) => (
          <button
            key={mode}
            className={viewspecs.showContent === mode ? "active" : ""}
            onClick={() => onChange({ showContent: mode })}
          >
            {mode === "first-line" ? "1st line" : mode}
          </button>
        ))}
      </div>

      {/* Toggles */}
      <h3>Display</h3>
      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
        <button
          className={viewspecs.showSIDs ? "active" : ""}
          onClick={() => onChange({ showSIDs: !viewspecs.showSIDs })}
        >
          SIDs
        </button>
        <button
          className={viewspecs.indentation ? "active" : ""}
          onClick={() => onChange({ indentation: !viewspecs.indentation })}
        >
          Indent
        </button>
        <button
          className={viewspecs.showBlankLines ? "active" : ""}
          onClick={() =>
            onChange({ showBlankLines: !viewspecs.showBlankLines })
          }
        >
          Spacing
        </button>
      </div>

      {/* Numbering */}
      <h3>Numbering</h3>
      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
        {(["sid", "sequential", "none"] as const).map((mode) => (
          <button
            key={mode}
            className={viewspecs.numbering === mode ? "active" : ""}
            onClick={() => onChange({ numbering: mode })}
          >
            {mode === "sid" ? "SID" : mode === "sequential" ? "Seq" : "None"}
          </button>
        ))}
      </div>

      {/* Filter */}
      <h3>Filter</h3>
      <div style={{ display: "flex", gap: "4px" }}>
        <input
          type="text"
          value={filterInput}
          onChange={(e) => setFilterInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              onChange({ filterText: filterInput || null });
            }
          }}
          placeholder="Enter text, press Enter"
        />
      </div>
      {viewspecs.filterText && (
        <button
          onClick={() => {
            onChange({ filterText: null });
            setFilterInput("");
          }}
          style={{ marginTop: "4px" }}
        >
          Clear filter
        </button>
      )}

      {/* Frozen */}
      {viewspecs.showFrozen && (
        <>
          <h3>Zoom</h3>
          <div>
            <span style={{ color: "var(--nls-command)", fontSize: "0.85em" }}>
              Frozen @{viewspecs.showFrozen}
            </span>
            <button
              onClick={() => onChange({ showFrozen: null })}
              style={{ marginLeft: "8px" }}
            >
              Unfreeze
            </button>
          </div>
        </>
      )}

      {/* Split */}
      <h3>Split</h3>
      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
        {(["none", "horizontal", "vertical"] as const).map((mode) => (
          <button
            key={mode}
            className={viewspecs.splitMode === mode ? "active" : ""}
            onClick={() => onChange({ splitMode: mode })}
          >
            {mode === "none" ? "Off" : mode === "horizontal" ? "H" : "V"}
          </button>
        ))}
        {viewspecs.splitMode !== "none" && (
          <button
            className={viewspecs.splitLinked ? "active" : ""}
            onClick={() => onChange({ splitLinked: !viewspecs.splitLinked })}
          >
            Linked
          </button>
        )}
      </div>

      {/* Presets */}
      <h3>Presets</h3>
      <div style={{ display: "flex", gap: "4px", flexWrap: "wrap" }}>
        {VIEWSPEC_PRESETS.map((preset) => (
          <button
            key={preset.name}
            onClick={() => onChange(preset.viewspecs)}
            title={preset.description}
          >
            {preset.label}
          </button>
        ))}
      </div>
    </div>
  );
}
